import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { apiHeadersOrThrow } from '../../lib/authHeader'
import './ManualReviewQueue.css'

/* An issue was once a bare string and is now { code, severity, message }. Both shapes are
   in the table, so both are read: rewriting the old rows to match the new code would be
   changing a record of what was found at the time, which is not ours to do. */
type Severity = 'reject' | 'flag' | 'note'

interface ReviewIssue {
  code: string
  severity: Severity
  message: string
}

function asIssues(raw: unknown): ReviewIssue[] {
  if (!Array.isArray(raw)) return []
  return raw.map((entry) => {
    if (typeof entry === 'string') return { code: '', severity: 'flag' as Severity, message: entry }
    const e = (entry || {}) as { code?: string; severity?: string; message?: string }
    const severity = e.severity === 'reject' || e.severity === 'note' ? e.severity : 'flag'
    return { code: e.code || '', severity: severity as Severity, message: e.message || JSON.stringify(entry) }
  })
}

/* An expired identity document is the one finding a reviewer may not wave through, so it
   is computed here rather than trusted to be noticed among the other issues. */
function expiredOn(doc: { extractedExpiry: string | null; validationIssues: ReviewIssue[] }): string | null {
  const flagged = doc.validationIssues.find((i) => i.code === 'expired')
  const today = new Date().toISOString().slice(0, 10)
  if (doc.extractedExpiry && doc.extractedExpiry < today) return doc.extractedExpiry
  if (flagged) return doc.extractedExpiry || 'an earlier date'
  return null
}

/* The name chain is the check that actually stops the loss: the name on the ID, the name
   on the registration certificate and the name on the bank account all have to be the
   same person. This compares one document against the name on the deal and is generous
   about form - order, case, titles - and strict about content. It raises a question for a
   reviewer; it never answers one. */
function nameAgreement(onDeal: string, onDocument: string): 'same' | 'partial' | 'different' | 'unknown' {
  const tokens = (v: string) =>
    String(v || '')
      .toLowerCase()
      .replace(/[^a-z0-9 ]+/g, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .filter((t) => !['mr', 'mrs', 'ms', 'miss', 'dr', 'pty', 'ltd', 'limited', 'the'].includes(t))
  const a = tokens(onDeal)
  const b = tokens(onDocument)
  if (!a.length || !b.length) return 'unknown'
  if (a.slice().sort().join(' ') === b.slice().sort().join(' ')) return 'same'
  const shared = a.filter((t) => b.includes(t))
  return shared.length ? 'partial' : 'different'
}

/*
 * The five official state registration checkers, from Winnie's review. There is
 * no national register and no API, so these are links a reviewer follows by hand.
 */
const REGO_CHECK_SITES = [
  { state: 'NSW', url: 'https://check-registration.service.nsw.gov.au/frc?isLoginRequired=true' },
  {
    state: 'QLD',
    url: 'https://www.service.transport.qld.gov.au/checkrego/application/VehicleSearch.xhtml?dswid=-1859',
  },
  {
    state: 'VIC',
    url: 'https://www.vicroads.vic.gov.au/registration/buy-sell-or-transfer-a-vehicle/check-vehicle-registration/vehicle-registration-enquiry/',
  },
  { state: 'WA', url: 'https://online.transport.wa.gov.au/webExternal/registration/?1' },
  { state: 'SA', url: 'https://account.ezyreg.sa.gov.au/account/check-registration.htm' },
]

interface DocumentForReview {
  id: string
  dealId: string
  documentType: string
  documentFilePath: string
  uploadedAt: string
  manualReviewReason: string
  /** null when no automated extraction ran — which is currently every document. */
  validationConfidence: number | null
  extractedName: string
  extractedAddress: string
  validationIssues: ReviewIssue[]
  extractedExpiry: string | null
  /* Null means nothing has been marked as expired. A date in the past is a hard stop that
     no reviewer may clear. */
  reviewedByUserId: string | null
  sellerLegalName: string
  vehicleInfo: { year: number; make: string; model: string }
  sellerInfo: { name: string; email: string }
}

interface FilterState {
  status: 'all' | 'manual_confirm' | 'hard_block'
  sortBy: 'confidence' | 'date' | 'seller'
}

export default function ManualReviewQueue() {
  const [documents, setDocuments] = useState<DocumentForReview[]>([])
  const [filter, setFilter] = useState<FilterState>({ status: 'all', sortBy: 'confidence' })
  const [selectedDoc, setSelectedDoc] = useState<DocumentForReview | null>(null)
  const [showReviewModal, setShowReviewModal] = useState(false)
  const [reviewDecision, setReviewDecision] = useState<'approved' | 'rejected' | null>(null)
  const [reviewReason, setReviewReason] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [docUrl, setDocUrl] = useState<string | null>(null)
  const [docUrlError, setDocUrlError] = useState<string | null>(null)
  const [recheckingId, setRecheckingId] = useState<string | null>(null)
  const [recheckNote, setRecheckNote] = useState<{ id: string; text: string } | null>(null)
  const [allDocs, setAllDocs] = useState<DocumentForReview[]>([])

  useEffect(() => {
    fetchDocumentsForReview()
  }, [filter])

  const fetchDocumentsForReview = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('dd_documents')
        .select('*, deals(autograb_year, autograb_make, autograb_model, seller_name, seller_legal_name, seller_email)')
        .in('validation_status', ['manual_review', 'hard_block'])
        .is('manual_review_required', true)
        .order('validation_confidence', { ascending: true })

      if (error) throw error

      const formatted: DocumentForReview[] = (data || []).map((doc: any) => ({
        id: doc.id,
        dealId: doc.deal_id,
        documentType: doc.document_type,
        documentFilePath: doc.document_file_path,
        uploadedAt: doc.uploaded_at,
        manualReviewReason: doc.manual_review_reason || '',
        validationConfidence:
          doc.validation_confidence === null || doc.validation_confidence === undefined
            ? null
            : Number(doc.validation_confidence),
        extractedName: doc.extracted_name || '',
        extractedAddress: doc.extracted_address || '',
        validationIssues: asIssues(doc.validation_issues),
        extractedExpiry: doc.extracted_expiry_date || null,
        reviewedByUserId: doc.reviewed_by_user_id || null,
        sellerLegalName: doc.deals?.seller_legal_name || doc.deals?.seller_name || '',
        vehicleInfo: {
          year: doc.deals?.autograb_year || 0,
          make: doc.deals?.autograb_make || '',
          model: doc.deals?.autograb_model || '',
        },
        sellerInfo: { name: doc.deals?.seller_name || '', email: doc.deals?.seller_email || '' },
      }))

      setAllDocs(formatted)

      let filtered = formatted
      if (filter.status !== 'all') {
        filtered = filtered.filter((d) =>
          filter.status === 'manual_confirm'
            ? d.validationConfidence !== null && d.validationConfidence >= 85
            : d.validationConfidence === null || d.validationConfidence < 85
        )
      }
      filtered = [...filtered].sort((a, b) => {
        switch (filter.sortBy) {
          case 'confidence':
            return (a.validationConfidence ?? -1) - (b.validationConfidence ?? -1)
          case 'seller':
            return a.sellerInfo.name.localeCompare(b.sellerInfo.name)
          case 'date':
            // Was the `default: return 0` arm, so "Sort: Recent First" did nothing.
            return new Date(b.uploadedAt || 0).getTime() - new Date(a.uploadedAt || 0).getTime()
          default:
            return 0
        }
      })

      setDocuments(filtered)
      setLoadError(null)
    } catch (error) {
      // Was console.error only, so a broken compliance queue looked exactly like
      // a clear one: "No documents awaiting review".
      console.error('Error fetching documents:', error)
      setLoadError(error instanceof Error ? error.message : 'Could not load the review queue')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Reviewers were being asked to approve or reject a driver licence, bank
   * statement or ASIC extract while being shown only text — document_file_path
   * was fetched and never rendered. This resolves it to a viewable URL.
   */
  useEffect(() => {
    let cancelled = false
    setDocUrl(null)
    setDocUrlError(null)
    if (!selectedDoc?.documentFilePath) return
    ;(async () => {
      const { data, error } = await supabase.storage
        .from('dd-documents')
        .createSignedUrl(selectedDoc.documentFilePath, 300)
      if (cancelled) return
      if (error || !data?.signedUrl) setDocUrlError('Could not open the uploaded file.')
      else setDocUrl(data.signedUrl)
    })()
    return () => {
      cancelled = true
    }
  }, [selectedDoc])

  const submitReview = async () => {
    if (!selectedDoc || !reviewDecision) return

    /* The decision is recorded by the server, not from here.

       This screen used to write to dd_documents itself, which made both of the rules that
       matter advisory: whether the person is allowed to clear a flag, and whether the
       document has expired. A disabled button describes what the screen offers; it does not
       describe what the system permits. Both are now decided at /api/dd/review, where they
       hold whatever the page does. The checks below stay because telling someone early is
       kinder than a round trip - they are the courtesy, not the rule. */
    if (reviewDecision === 'approved') {
      const expired = expiredOn(selectedDoc)
      if (expired) {
        setSubmitError(
          'This document expired on ' + expired + '. An expired document cannot be approved. ' +
            'Ask the seller for a current one.'
        )
        return
      }
    }
    if (reviewDecision === 'rejected' && reviewReason.trim().length < 3) {
      setSubmitError('Please say why it was rejected - the seller has to be told what to send instead.')
      return
    }

    setSubmitError(null)
    setConfirming(false)
    setIsSubmitting(true)
    try {
      const headers = await apiHeadersOrThrow({ "Content-Type": "application/json" })
      const res = await fetch('/api/dd/review', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          documentId: selectedDoc.id,
          decision: reviewDecision,
          reason: reviewReason.trim(),
        }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok || !body?.success) {
        setSubmitError(body?.error || "The decision was not recorded. Please try again.")
        return
      }

      setSelectedDoc(null)
      setReviewDecision(null)
      setReviewReason('')
      await fetchDocumentsForReview()
    } catch (err) {
      setSubmitError((err as Error)?.message || "The decision was not recorded. Please try again.")
    } finally {
      setIsSubmitting(false)
    }
  }

  const getConfidenceColor = (c: number | null): string =>
    c === null ? '#9fb0b9' : c >= 90 ? '#4CAF50' : c >= 85 ? '#FFC107' : '#f44336'

  // A document with no automated assessment used to fall through to 0% and get
  // badged "HARD BLOCK"; a hard_block that kept its initial confidence of 100
  // got badged "PASS (Auto)". Both were wrong.
  const getStatusLabel = (c: number | null): string =>
    c === null ? 'NEEDS REVIEW' : c >= 99 ? 'PASS (Auto)' : c >= 85 ? 'MANUAL CONFIRM' : 'HARD BLOCK'

  const confidenceLabel = (c: number | null) => (c === null ? 'Not assessed' : `${c}%`)

  // Stats are computed from the unfiltered set — computing them from the filtered
  // list made the "Manual Confirm" counter read 0 whenever you filtered to hard
  // blocks, which is the opposite of what a stats row is for.
  /* The counters have to agree with the badges, and they did not. They were bands of
     the confidence score, while the badge follows the worst issue actually found. A
     document nobody could read scores 0, so it was being counted as a hard block: the
     row said a deal was stopped when nothing was stopped, it just needed reading.
     Same rule in both places now. */
  const stats = {
    total: allDocs.length,
    manualConfirm: allDocs.filter(
      (d) => d.validationIssues.length > 0 && !d.validationIssues.some((i) => i.severity === 'reject')
    ).length,
    hardBlock: allDocs.filter((d) => d.validationIssues.some((i) => i.severity === 'reject')).length,
  }
  /* Asking for a document to be read again.

     This is not a decision. It re-runs the automatic checks over the file the seller
     actually sent and replaces what they found - which matters because a finding is
     about the day it was made, and documents sit in this queue flagged only because,
     on the day they arrived, there was nothing here that could read them.

     The server refuses if a person has already decided the document, so nothing on
     this screen can quietly undo somebody's call. */
  const recheckDocument = async (documentId: string) => {
    setRecheckingId(documentId)
    setRecheckNote(null)
    try {
      const headers = await apiHeadersOrThrow({ 'Content-Type': 'application/json' })
      const res = await fetch('/api/dd/recheck', {
        method: 'POST',
        headers,
        body: JSON.stringify({ documentId }),
      })
      const body = await res.json().catch(() => null)
      if (!res.ok || !body || !body.success) {
        setRecheckNote({ id: documentId, text: (body && body.error) || 'It could not be checked again.' })
        return
      }
      setRecheckNote({
        id: documentId,
        text: body.after && body.after.textRead
          ? (body.changed ? 'Read. It now reads as ' + body.after.status + '.' : 'Read. The finding did not change.')
          : 'Checked again, and still nothing on it could be read.',
      })
      await fetchDocumentsForReview()
    } catch (err) {
      setRecheckNote({ id: documentId, text: (err as Error).message || 'It could not be checked again.' })
    } finally {
      setRecheckingId(null)
    }
  }


  if (loading) {
    return (
      <div className="review-queue-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading documents for review...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="review-queue-container">
      <div className="review-queue-header">
        <h2>Due Diligence Review Queue</h2>
        <div className="queue-stats">
          <div className="stat">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total</span>
          </div>
          <div className="stat">
            <span className="stat-value" style={{ color: '#FFC107' }}>{stats.manualConfirm}</span>
            <span className="stat-label">Manual Confirm</span>
          </div>
          <div className="stat">
            <span className="stat-value" style={{ color: '#f44336' }}>{stats.hardBlock}</span>
            <span className="stat-label">Hard Block</span>
          </div>
        </div>
      </div>

      <div className="review-controls">
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value as any })} className="filter-select">
          <option value="all">All Documents</option>
          <option value="manual_confirm">Manual Confirm Only</option>
          <option value="hard_block">Hard Blocks Only</option>
        </select>
        <select value={filter.sortBy} onChange={(e) => setFilter({ ...filter, sortBy: e.target.value as any })} className="sort-select">
          <option value="confidence">Sort: Lowest Confidence</option>
          <option value="seller">Sort: Seller Name</option>
          <option value="date">Sort: Recent First</option>
        </select>
      </div>

      {loadError && (
        <div className="queue-error" role="alert">
          <span>{loadError}</span>
          <button className="btn-cancel" onClick={fetchDocumentsForReview}>Retry</button>
        </div>
      )}

      {documents.length === 0 ? (
        <div className="empty-state">
          {loadError ? (
            <p>The queue could not be loaded — this is not the same as it being empty.</p>
          ) : allDocs.length > 0 ? (
            <>
              <p>No documents match this filter ({allDocs.length} awaiting review in total)</p>
              <button className="btn-cancel" onClick={() => setFilter({ ...filter, status: 'all' })}>
                Show all documents
              </button>
            </>
          ) : (
            <p>No documents awaiting review</p>
          )}
        </div>
      ) : (
        <div className="review-documents">
          {documents.map((doc) => (
            <div key={doc.id} className="review-card">
              <div className="card-header">
                <div className="document-meta">
                  <h4>{doc.documentType}</h4>
                  <p className="vehicle-info">
                    {doc.vehicleInfo.year} {doc.vehicleInfo.make} {doc.vehicleInfo.model}
                  </p>
                  <p className="seller-info">{doc.sellerInfo.name}</p>
                </div>
                <div className="confidence-badge" style={{ borderColor: getConfidenceColor(doc.validationConfidence) }}>
                  {/* A document nobody could read is not blocked, it is unread - and calling it a hard
                      block tells a reviewer the wrong thing about what to do next. So the badge follows
                      the worst thing actually found, and only falls back to the confidence score when the
                      checks recorded nothing. */}
                  <div className="confidence-value">
                    {doc.validationIssues.some((i) => i.severity === "reject")
                      ? "CANNOT ACCEPT"
                      : doc.validationIssues.length
                        ? "NEEDS A LOOK"
                        : confidenceLabel(doc.validationConfidence)}
                  </div>
                  <div className="confidence-label" style={{ color: getConfidenceColor(doc.validationConfidence) }}>
                    {doc.validationIssues.length
                      ? doc.validationIssues.length === 1
                        ? "1 issue"
                        : doc.validationIssues.length + " issues"
                      : getStatusLabel(doc.validationConfidence)}
                  </div>
                </div>
              </div>

              <div className="extracted-data">
                <div className="data-item">
                  <span className="data-label">Name:</span>
                  <span className="data-value">{doc.extractedName || '—'}</span>
                </div>
                <div className="data-item">
                  <span className="data-label">Address:</span>
                  <span className="data-value">{doc.extractedAddress || '—'}</span>
                </div>
              </div>

              {doc.validationIssues && doc.validationIssues.length > 0 && (
                <div className="issues-section">
                  <h5>Issues Detected:</h5>
                  <ul>
                    {doc.validationIssues.map((issue, idx) => (
                      <li key={idx} className={`issue-${issue.severity}`}>
                        {issue.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="card-actions">
                <button
                  className="btn-review"
                  onClick={() => {
                    setSelectedDoc(doc)
                    setShowReviewModal(true)
                  }}
                >
                  Review &amp; Decide
                </button>
                {/* A quiet second action beside the one that decides. Re-reading a
                    document is reversible and changes nothing about who approved what,
                    so it does not belong behind the review modal. */}
                <button
                  className="btn-recheck"
                  disabled={recheckingId === doc.id}
                  onClick={(event) => {
                    event.stopPropagation()
                    void recheckDocument(doc.id)
                  }}
                >
                  {recheckingId === doc.id ? 'Reading it…' : 'Check it again'}
                </button>
                {recheckNote && recheckNote.id === doc.id && (
                  <div className="recheck-note">{recheckNote.text}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showReviewModal && selectedDoc && (
        <div
          className="modal-overlay"
          onClick={() => {
            // Clicking the backdrop used to silently discard whatever the
            // reviewer had typed into the notes box.
            if (reviewReason.trim() && !window.confirm('Discard your review notes?')) return
            setShowReviewModal(false)
            setConfirming(false)
          }}
        >
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Review Document: {selectedDoc.documentType}</h3>
            <div className="review-details">
              <p><strong>Vehicle:</strong> {selectedDoc.vehicleInfo.year} {selectedDoc.vehicleInfo.make} {selectedDoc.vehicleInfo.model}</p>
              <p><strong>Seller:</strong> {selectedDoc.sellerInfo.name}</p>
              <p><strong>Confidence:</strong> {confidenceLabel(selectedDoc.validationConfidence)}</p>
              {selectedDoc.manualReviewReason && (
                <p><strong>Why it is here:</strong> {selectedDoc.manualReviewReason}</p>
              )}
              <p><strong>Extracted Name:</strong> {selectedDoc.extractedName || '—'}</p>
              <p><strong>Extracted Address:</strong> {selectedDoc.extractedAddress || '—'}</p>

              {/*
                Winnie's review asked for the state registration checkers here. The reviewer
                is holding a rego certificate and needs to put it against the register that
                issued it. Each state runs its own and there is no API behind any of them, so
                this is a person looking something up in a new tab - which is why nothing on
                this screen records a result from it.
              */}
              <div className="mrq-rego-checks">
                <strong>Check the registration:</strong>
                {REGO_CHECK_SITES.map((site) => (
                  <a
                    key={site.state}
                    href={site.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {site.state}
                  </a>
                ))}
              </div>

              {/* The name chain. Shown as a comparison rather than a verdict, because the deal
                  record is as likely to be the thing that is wrong as the document is. */}
              <div className="name-chain">
                <p>
                  <strong>Name on the deal:</strong> {selectedDoc.sellerLegalName || "not recorded"}
                </p>
                <p>
                  <strong>Name on this document:</strong>{" "}
                  {selectedDoc.extractedName || "nothing was read off it"}
                </p>
                {(() => {
                  const verdict = nameAgreement(selectedDoc.sellerLegalName, selectedDoc.extractedName)
                  const said = {
                    same: "These agree.",
                    partial: "These only partly agree - worth a look before you clear it.",
                    different: "These do not look like the same name.",
                    unknown: "There is not enough here to compare.",
                  }[verdict]
                  return <p className={`chain-${verdict}`}>{said}</p>
                })()}
              </div>

              {expiredOn(selectedDoc) ? (
                <div className="expired-block" role="alert">
                  <strong>This document expired on {expiredOn(selectedDoc)}.</strong>{" "}
                  An expired document cannot be accepted, and this is not something a reviewer can
                  waive. Ask the seller for a current one.
                </div>
              ) : null}
            </div>

            <div className="document-preview">
              <div className="document-preview-head">
                <strong>Uploaded document</strong>
                {docUrl && (
                  <a href={docUrl} target="_blank" rel="noopener noreferrer" className="doc-open-link">
                    Open in a new tab
                  </a>
                )}
              </div>
              {docUrlError ? (
                <p className="doc-preview-error">{docUrlError}</p>
              ) : docUrl ? (
                <iframe title="Uploaded document" src={docUrl} className="doc-frame" />
              ) : (
                <p className="doc-preview-error">Loading the uploaded file…</p>
              )}
            </div>

            <div className="decision-section">
              <label className="decision-option">
                <input type="radio" name="decision" checked={reviewDecision === 'approved'} onChange={() => setReviewDecision('approved')} disabled={Boolean(expiredOn(selectedDoc))} />
                <span>✓ Approve &amp; Validate</span>
              </label>
              <label className="decision-option">
                <input type="radio" name="decision" checked={reviewDecision === 'rejected'} onChange={() => setReviewDecision('rejected')} />
                <span>✗ Reject &amp; Request More</span>
              </label>
            </div>
            <div className="reason-section">
              {/*
                Winnie, 1 Sep: reviewers were typing the same rejection reason out every
                time. These are the common ones - clicking appends rather than replaces,
                so a reviewer can stack two reasons or add their own wording after.
              */}
              <div className="review-reason-picks">
                {[
                  'Account name must be in the registered owner\'s name.',
                  'This document has expired - please send a current one.',
                  'The image is not readable - please send a clearer photo.',
                  'The name on this document does not match the seller on the deal.',
                  'This is not the document we asked for.',
                  'Only part of the document came through - please send all pages.',
                ].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    className="review-reason-pick"
                    onClick={() =>
                      setReviewReason((prev) =>
                        prev.trim() ? `${prev.trim()} ${preset}` : preset,
                      )
                    }
                  >
                    {preset}
                  </button>
                ))}
              </div>
              <label htmlFor="reason">Additional Notes (optional):</label>
              <textarea
                id="reason"
                value={reviewReason}
                onChange={(e) => setReviewReason(e.target.value)}
                placeholder="Document quality concerns, data discrepancies, etc."
                className="reason-input"
              />
            </div>
            {submitError && <div className="queue-error" role="alert">{submitError}</div>}

            {confirming ? (
              <div className="confirm-review">
                <p>
                  {reviewDecision === 'approved'
                    ? 'Approving this document may advance the deal to Committed. This cannot be undone from this screen.'
                    : 'Rejecting removes this document from the queue. The seller will need to upload a replacement.'}
                </p>
                <div className="modal-actions">
                  <button className="btn-submit-review" onClick={submitReview} disabled={isSubmitting}>
                    {isSubmitting ? 'Submitting...' : `Yes, ${reviewDecision === 'approved' ? 'approve' : 'reject'} it`}
                  </button>
                  <button className="btn-cancel" onClick={() => setConfirming(false)} disabled={isSubmitting}>
                    Back
                  </button>
                </div>
              </div>
            ) : (
              <div className="modal-actions">
                <button
                  className="btn-submit-review"
                  onClick={() => setConfirming(true)}
                  disabled={!reviewDecision || isSubmitting}
                >
                  Submit Review
                </button>
                <button
                  className="btn-cancel"
                  onClick={() => {
                    setShowReviewModal(false)
                    setConfirming(false)
                    setSubmitError(null)
                  }}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
